// src/api/tasksApi.ts

import { baseApi } from './baseApi';
import type {
  Task,
  TasksResponse,
  CreateTaskRequest,
  UpdateTaskRequest,
  UpdateTaskStatusRequest,
  ListTasksParams,
  TaskChecklistItem,
  CreateChecklistItemRequest,
  UpdateChecklistItemRequest,
  ReorderChecklistItemsRequest,
} from '../types/api';
import { normalizeTasksListResponse } from '../utils/listNormalizers';

export const tasksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTasks: builder.query<TasksResponse, ListTasksParams>({
      query: (params) => ({
        url: '/tasks',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
          ...(params.status && { status: params.status }),
          ...(params.assigneeUserId && { assigneeUserId: params.assigneeUserId }),
          ...(params.relatedContactId && { relatedContactId: params.relatedContactId }),
          ...(params.projectId && { projectId: params.projectId }),
          ...(params.milestoneId && { milestoneId: params.milestoneId }),
          ...(params.search && { search: params.search }),
          ...(params.limit && { limit: params.limit }),
          ...(params.offset && { offset: params.offset }),
        },
      }),
      transformResponse: (response: unknown) => normalizeTasksListResponse(response),
      providesTags: (result, error, params) =>
        result
          ? [
              ...result.tasks.map(({ id }) => ({ type: 'Task' as const, id })),
              { type: 'Task', id: params.workspaceId || 'LIST' },
            ]
          : [{ type: 'Task', id: params.workspaceId || 'LIST' }],
    }),

    getTask: builder.query<Task, string>({
      query: (id) => `/tasks/${id}`,
      providesTags: (result, error, id) => [{ type: 'Task', id }],
    }),

    createTask: builder.mutation<Task, CreateTaskRequest>({
      query: (body) => ({
        url: '/tasks',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'Task', id: workspaceId || 'LIST' },
      ],
    }),

    updateTask: builder.mutation<Task, { id: string } & UpdateTaskRequest>({
      query: ({ id, ...body }) => ({
        url: `/tasks/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Task', id },
        { type: 'Task', id: 'LIST' },
      ],
    }),

    updateTaskStatus: builder.mutation<Task, { id: string } & UpdateTaskStatusRequest>({
      query: ({ id, status }) => ({
        url: `/tasks/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Task', id },
        { type: 'Task', id: 'LIST' },
      ],
    }),

    deleteTask: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/tasks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Task', id },
        { type: 'Task', id: 'LIST' },
      ],
    }),

    // Checklist endpoints
    getChecklist: builder.query<TaskChecklistItem[], string>({
      query: (taskId) => `/tasks/${taskId}/checklist`,
      providesTags: (result, error, taskId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'TaskChecklistItem' as const, id })),
              { type: 'TaskChecklistItem', id: `TASK-${taskId}` },
            ]
          : [{ type: 'TaskChecklistItem', id: `TASK-${taskId}` }],
    }),

    createChecklistItem: builder.mutation<
      TaskChecklistItem,
      { taskId: string } & CreateChecklistItemRequest
    >({
      query: ({ taskId, ...body }) => ({
        url: `/tasks/${taskId}/checklist`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskChecklistItem', id: `TASK-${taskId}` },
        { type: 'Task', id: taskId },
      ],
    }),

    updateChecklistItem: builder.mutation<
      TaskChecklistItem,
      { taskId: string; itemId: string } & UpdateChecklistItemRequest
    >({
      query: ({ taskId, itemId, ...body }) => ({
        url: `/tasks/${taskId}/checklist/${itemId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { taskId, itemId }) => [
        { type: 'TaskChecklistItem', id: itemId },
        { type: 'TaskChecklistItem', id: `TASK-${taskId}` },
      ],
    }),

    deleteChecklistItem: builder.mutation<{ success: boolean }, { taskId: string; itemId: string }>({
      query: ({ taskId, itemId }) => ({
        url: `/tasks/${taskId}/checklist/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { taskId, itemId }) => [
        { type: 'TaskChecklistItem', id: itemId },
        { type: 'TaskChecklistItem', id: `TASK-${taskId}` },
        { type: 'Task', id: taskId },
      ],
    }),

    toggleChecklistItem: builder.mutation<TaskChecklistItem, { taskId: string; itemId: string }>({
      query: ({ taskId, itemId }) => ({
        url: `/tasks/${taskId}/checklist/${itemId}/toggle`,
        method: 'POST',
      }),
      // Optimistic update for toggle
      async onQueryStarted({ taskId, itemId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          tasksApi.util.updateQueryData('getChecklist', taskId, (draft) => {
            const item = draft.find((i) => i.id === itemId);
            if (item) {
              item.completed = !item.completed;
              item.completedAt = item.completed ? new Date().toISOString() : null;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { taskId, itemId }) => [
        { type: 'TaskChecklistItem', id: itemId },
        { type: 'TaskChecklistItem', id: `TASK-${taskId}` },
      ],
    }),

    reorderChecklistItems: builder.mutation<
      TaskChecklistItem[],
      { taskId: string } & ReorderChecklistItemsRequest
    >({
      query: ({ taskId, ...body }) => ({
        url: `/tasks/${taskId}/checklist/reorder`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskChecklistItem', id: `TASK-${taskId}` },
      ],
    }),
  }),
});

export const {
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useUpdateTaskStatusMutation,
  useDeleteTaskMutation,
  // Checklist hooks
  useGetChecklistQuery,
  useCreateChecklistItemMutation,
  useUpdateChecklistItemMutation,
  useDeleteChecklistItemMutation,
  useToggleChecklistItemMutation,
  useReorderChecklistItemsMutation,
} = tasksApi;
